import { API_BASE as API_BASE_CONFIG } from '../../config'
const API_BASE = API_BASE_CONFIG

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('xlr8_token') || ''}`,
  'Content-Type': 'application/json',
})

export async function fetchCustomerProfile(customerId, filters = {}, systemId = null) {
  // Build query params from active filters
  const params = new URLSearchParams()
  for (const [key, values] of Object.entries(filters)) {
    if (values && values.length > 0) {
      params.append(key, values.join(','))
    }
  }
  if (systemId) {
    params.append('system', systemId)
  }
  const qs = params.toString()
  const url = `${API_BASE}/api/customers/${customerId}/profile${qs ? `?${qs}` : ''}`
  const res = await fetch(url, { headers: authHeaders() })
  // 404 = customer exists but no data tables loaded yet — not an error
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`Profile fetch failed: ${res.status}`)
  return res.json()
}

export async function fetchProfileFilters(customerId) {
  const res = await fetch(`${API_BASE}/api/customers/${customerId}/profile/filters`, {
    headers: authHeaders(),
  })
  if (!res.ok) return null
  return res.json()
}

export async function fetchGeoDrilldown(customerId, countryCode) {
  const res = await fetch(`${API_BASE}/api/customers/${customerId}/geo-drilldown/${countryCode}`, {
    headers: authHeaders(),
  })
  // A failed read throws: it is not "no regions, total 0" (item 201).
  if (!res.ok) throw new Error(`Region breakdown failed: ${res.status}`)
  return res.json()
}

// ─── Drill-Down API ──────────────────────────────────────
export async function fetchDrillSummary(customerId, queryId, filters = null) {
  const res = await fetch(`${API_BASE}/api/drilldown/${customerId}/summary`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ query_id: queryId, filters }),
  })
  if (!res.ok) return null
  return res.json()
}

export async function fetchDrillDetail(customerId, queryId, filters = null, limit = 200, offset = 0) {
  const res = await fetch(`${API_BASE}/api/drilldown/${customerId}/detail`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ query_id: queryId, filters, limit, offset }),
  })
  if (!res.ok) {
    if (res.status === 400 || res.status === 404) {
      try {
        await fetch(`${API_BASE}/api/customers/${customerId}/profile`, { headers: authHeaders() })
        const retry = await fetch(`${API_BASE}/api/drilldown/${customerId}/detail`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ query_id: queryId, filters, limit, offset }),
        })
        if (retry.ok) return retry.json()
      } catch (_) { /* fall through */ }
    }
    return null
  }
  return res.json()
}

export async function fetchDrillExportJson(customerId, queryId, filters = null, maxRows = 50000) {
  const res = await fetch(`${API_BASE}/api/drilldown/${customerId}/export/json`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ query_id: queryId, filters, max_rows: maxRows }),
  })
  if (!res.ok) return null
  return res.json()
}

// ─── Finding status (findings status v2) ──────────────────
// One function per action route. Who did it is the signed-in user (the server never
// takes it from the body); the server decides who may, from which status, and whether
// a reason is required. Returns { ok, status, data | error }.
async function postFindingAction(path, reason) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ reason: reason || null }),
  })
  let data = null
  try { data = await res.json() } catch { /* empty body */ }
  if (!res.ok) {
    const detail = typeof data?.detail === 'string' ? data.detail : `HTTP ${res.status}`
    return { ok: false, status: res.status, error: detail }
  }
  return { ok: true, status: res.status, data }
}

export const acknowledgeFinding = (observationId, reason) =>
  postFindingAction(`/api/observations/${observationId}/acknowledge`, reason)
export const submitFixForFinding = (observationId, reason) =>
  postFindingAction(`/api/observations/${observationId}/fix-submitted`, reason)
export const requestFindingDismissal = (observationId, reason) =>
  postFindingAction(`/api/observations/${observationId}/dismissal-request`, reason)
export const declineFindingDismissal = (observationId, reason) =>
  postFindingAction(`/api/observations/${observationId}/dismissal-request/decline`, reason)
export const dismissFinding = (observationId, reason) =>
  postFindingAction(`/api/observations/${observationId}/dismiss`, reason)
export const reopenFinding = (observationId, reason) =>
  postFindingAction(`/api/observations/${observationId}/reopen`, reason)

// Resolved and dismissed findings: the profile lists the active ones only (open,
// acknowledged, fix submitted). A customer gets reviewed checks only, no SQL.
export async function fetchClosedFindings(customerId) {
  const res = await fetch(`${API_BASE}/api/observations/${customerId}?include_dismissed=true`, {
    headers: authHeaders(),
  })
  // A failed read throws: it is not "no resolved or dismissed findings" (item 201).
  if (!res.ok) throw new Error(`Closed findings fetch failed: ${res.status}`)
  const body = await res.json()
  return (body?.observations || [])
    .filter(o => o.status === 'resolved' || o.status === 'dismissed')
    .map(o => ({
      ...o,
      people_count: o.data?.people_count ?? null,
      subject_count: o.data?.subject_count ?? null,
      subject_noun: o.data?.subject_noun ?? null,
      subject_noun_one: o.data?.subject_noun_one ?? null,
      record_count: o.data?.count ?? o.affected_count ?? 0,
    }))
}
